import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, Package, ChevronDown, Settings, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import CartSheet from './CartSheet';

const LOGO = "https://customer-assets.emergentagent.com/job_peptide-research-hub-6/artifacts/kowhmc7f_Gemini_Generated_Image_tlwfymtlwfymtlwf__1_-removebg.png";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { itemCount, fetchCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [adminView, setAdminView] = useState(true);

  useEffect(() => { if (user) fetchCart(); }, [user, fetchCart]);
  useEffect(() => { setMobileMenuOpen(false); }, [location]);

  const handleLogout = async () => { await logout(); navigate('/'); };
  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* ─── PRE-ORDER ANNOUNCEMENT BAR ─────────────────── */}
      <Link
        to="/pre-orders"
        className="block bg-[hsl(210,52%,21%)] text-slate-50 hover:bg-[hsl(210,52%,17%)] transition-colors"
        data-testid="preorder-announcement-bar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-center gap-2 text-center">
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-amber-300">Pre-Order Wave 01</span>
          <span className="text-xs sm:text-sm font-body text-slate-100">
            Open through <span className="font-semibold underline decoration-1 underline-offset-2">June 30, 2026</span> — orders process &amp; COAs upload after the wave closes.
          </span>
          <span className="hidden sm:inline text-xs text-slate-300 ml-1">Read policy →</span>
        </div>
      </Link>
      <header className="sticky top-0 z-[60] backdrop-blur-xl bg-white/80 border-b border-[hsl(210,45%,40%)]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center group" data-testid="logo-link">
              <img src={LOGO} alt="Wilvada" className="h-[42px] sm:h-[46px] w-auto object-contain" />
            </Link>
            <nav className="hidden md:flex items-center gap-7">
              <Link to="/catalog" className="text-[13px] font-medium text-muted-foreground hover:text-[hsl(210,45%,40%)] transition-colors" data-testid="nav-catalog">Catalog</Link>
              <Link to="/catalog?product_type=peptide" className="text-[13px] font-medium text-muted-foreground hover:text-[hsl(210,45%,40%)] transition-colors" data-testid="nav-peptides">Peptides</Link>
              <Link to="/catalog?product_type=equipment" className="text-[13px] font-medium text-muted-foreground hover:text-[hsl(210,45%,40%)] transition-colors" data-testid="nav-equipment">Equipment</Link>
              <Link to="/faq" className="text-[13px] font-medium text-muted-foreground hover:text-[hsl(210,45%,40%)] transition-colors" data-testid="nav-faq">FAQ</Link>
              <Link to="/contact" className="text-[13px] font-medium text-muted-foreground hover:text-[hsl(210,45%,40%)] transition-colors" data-testid="nav-contact">Contact</Link>
            </nav>
            <div className="flex items-center gap-3">
              {/* Admin/User Toggle */}
              {isAdmin && (
                <Button variant="ghost" size="sm" className="gap-1.5 text-xs hidden md:inline-flex" onClick={() => setAdminView(!adminView)} data-testid="admin-user-toggle">
                  {adminView ? <ToggleRight className="h-4 w-4 text-[hsl(210,45%,40%)]" /> : <ToggleLeft className="h-4 w-4" />}
                  {adminView ? 'Admin' : 'User'}
                </Button>
              )}
              <Sheet open={cartOpen} onOpenChange={setCartOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="cart-button">
                    <ShoppingCart className="h-5 w-5" />
                    {itemCount > 0 && (<Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-[hsl(210,45%,40%)] text-white" data-testid="cart-count">{itemCount}</Badge>)}
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-md">
                  <SheetHeader><SheetTitle className="font-heading">Shopping Cart</SheetTitle></SheetHeader>
                  <CartSheet onClose={() => setCartOpen(false)} />
                </SheetContent>
              </Sheet>
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-trigger">
                      <User className="h-4 w-4" /><span className="hidden sm:inline text-sm">{user.name}</span><ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="nav-dashboard"><User className="h-4 w-4 mr-2" /> Dashboard</DropdownMenuItem>
                    {isAdmin && adminView && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin-dashboard"><Settings className="h-4 w-4 mr-2" /> Admin Dashboard</DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="logout-button"><LogOut className="h-4 w-4 mr-2" /> Sign Out</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="login-nav-button">Sign In</Button>
                  <Button size="sm" onClick={() => navigate('/register')} data-testid="register-nav-button" className="hidden sm:inline-flex bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white">Get Started</Button>
                </div>
              )}
              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-white/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-3">
              <Link to="/catalog" className="block text-sm font-medium text-foreground py-2">All Products</Link>
              <Link to="/catalog?product_type=peptide" className="block text-sm text-muted-foreground py-2">Peptides</Link>
              <Link to="/catalog?product_type=equipment" className="block text-sm text-muted-foreground py-2">Equipment</Link>
              <Link to="/faq" className="block text-sm text-muted-foreground py-2">FAQ</Link>
              <Link to="/contact" className="block text-sm text-muted-foreground py-2">Contact</Link>
            </div>
          </div>
        )}
      </header>
      <div data-testid="research-disclaimer-banner" className="sticky top-16 z-50 bg-[hsl(210,52%,21%)] text-white text-center py-2 px-4 text-xs tracking-[0.15em] uppercase font-semibold font-body">
        For Research Use Only. Not for Human or Veterinary Consumption.
      </div>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-slate-200">
        <div className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              <div className="md:col-span-2 flex items-start">
                <img src={LOGO} alt="Wilvada" className="h-[102px] sm:h-32 w-auto object-contain" />
              </div>
              <div>
                <h4 className="font-heading font-semibold text-sm mb-4 text-[hsl(210,52%,21%)]">Products</h4>
                <div className="space-y-2">
                  <Link to="/catalog" className="block text-sm text-slate-600 hover:text-[hsl(210,45%,40%)] transition-colors">Full Catalog</Link>
                  <Link to="/catalog?product_type=peptide" className="block text-sm text-slate-600 hover:text-[hsl(210,45%,40%)] transition-colors">Peptides</Link>
                  <Link to="/catalog?product_type=equipment" className="block text-sm text-slate-600 hover:text-[hsl(210,45%,40%)] transition-colors">Equipment</Link>
                </div>
              </div>
              <div>
                <h4 className="font-heading font-semibold text-sm mb-4 text-[hsl(210,52%,21%)]">Support</h4>
                <div className="space-y-2">
                  <Link to="/faq" className="block text-sm text-slate-600 hover:text-[hsl(210,45%,40%)] transition-colors">FAQ</Link>
                  <Link to="/contact" className="block text-sm text-slate-600 hover:text-[hsl(210,45%,40%)] transition-colors">Contact Us</Link>
                  <Link to="/references" className="block text-sm text-slate-600 hover:text-[hsl(210,45%,40%)] transition-colors" data-testid="footer-references-link">Reference Library</Link>
                </div>
              </div>
              <div>
                <h4 className="font-heading font-semibold text-sm mb-4 text-[hsl(210,52%,21%)]">Policies</h4>
                <div className="space-y-2">
                  <Link to="/terms" className="block text-sm text-slate-600 hover:text-[hsl(210,45%,40%)] transition-colors">Terms &amp; Conditions</Link>
                  <Link to="/privacy" className="block text-sm text-slate-600 hover:text-[hsl(210,45%,40%)] transition-colors">Privacy</Link>
                  <Link to="/shipping-policy" className="block text-sm text-slate-600 hover:text-[hsl(210,45%,40%)] transition-colors">Shipping</Link>
                  <Link to="/refunds" className="block text-sm text-slate-600 hover:text-[hsl(210,45%,40%)] transition-colors">Refunds &amp; Returns</Link>
                  <Link to="/pre-orders" className="block text-sm text-slate-600 hover:text-[hsl(210,45%,40%)] transition-colors" data-testid="footer-preorder-link">Pre-Orders</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-[hsl(210,52%,12%)] text-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-3">
            <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Research Notice & Disclaimer</h4>
            <p className="text-xs text-slate-400 leading-relaxed">All products offered by Wilvada LC are intended strictly for laboratory research and development purposes. Our products are for in-vitro and pre-clinical research only and are not for human or veterinary use, including but not limited to diagnosis, treatment, cure, or prevention of any disease.</p>
            <p className="text-xs text-slate-400 leading-relaxed">By purchasing from Wilvada LC, the customer acknowledges the inherent risks in handling these materials and certifies they are a qualified professional with the proper facilities and equipment to manage such risks. The buyer assumes all responsibility for complying with local regulations and safety guidelines. Wilvada LC reserves the right to cancel any order if misuse is suspected.</p>
            <p className="text-xs text-slate-400 leading-relaxed">Statements made on this website have not been evaluated by the U.S. Food and Drug Administration. Wilvada LC is a chemical supplier and is not a compounding pharmacy or outsourcing facility as defined under sections 503A or 503B of the Federal Food, Drug, and Cosmetic Act.</p>
            <p className="text-xs text-slate-300 font-semibold">WARNING: Strictly NOT for human consumption, medical, or cosmetic use.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
